import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

// COMPONENTS
import "react-toastify/dist/ReactToastify.css";
import { sentryWrappedException } from "~/utils/Utils";
import { LoadingLarge } from "~/utils/Loading";

const ToastContainer = React.lazy(() => import("../components/Toast/ToastContainer"));
const Navbar = React.lazy(() => import("../components/Navbar"));
const Footer = React.lazy(() => import("../components/Footer/Footer"));

// PAGES
const LandPage = React.lazy(() => import("../pages/Landing"));
const HomePage = React.lazy(() => import("../pages/Home"));
const ContractsPage = React.lazy(() => import("../pages/Contracts"));
const ContractPage = React.lazy(() => import("../pages/Contract"));
const CareerPage = React.lazy(() => import("../pages/Career"));
const PerformancePage = React.lazy(() => import("../pages/Performance"));
const PortfolioPage = React.lazy(() => import("../pages/Portfolio"));
const PortfolioTransactionPage = React.lazy(() => import("../pages/PortfolioTransaction"));
const CreatePortfolioPage = React.lazy(() => import("../pages/CreatePortfolio"));
const PortfolioContainerPage = React.lazy(() => import("../pages/PortfolioContainer"));
const PublicContractPage = React.lazy(() => import("../pages/PublicContract"));
const PublicRankingsPage = React.lazy(() => import("../pages/PublicRankings"));
const PublicAddressPage = React.lazy(() => import("../pages/PublicAddress"));
const PublicContractsPage = React.lazy(() => import("../pages/PublicContracts"));
// const PublicViewPage = React.lazy(() => import("../pages/PublicView"));
const StarWeekPage = React.lazy(() => import("../pages/StarWeek"));
const ProfilePage = React.lazy(() => import("../pages/Profile"));
const Wallets = React.lazy(() => import("../pages/Wallets"));
const CompleteAuth = React.lazy(() => import("~/pages/CompleteAuth"));

export default function MainRoutes() {
  const { isLoading, error } = useAuth0();

  const location = useLocation();

  React.useEffect(() => {
    // Kinda brute-forcing because otherwise it would go by the page's title, which is always "Parcision"
    window.gtag("event", "page_view", {
      page_title: location.pathname,
      page_location: location.pathname,
      page_path: location.pathname,
    });
  }, [location]);

  if (error) {
    sentryWrappedException(error);
    return <div>Oops... {error.message}</div>;
  }

  if (isLoading) {
    return (
      <>
        <Navbar />
        <LoadingLarge />
        <Footer />
      </>
    );
  }

  return (
    <>
      <ToastContainer />
      <Navbar />
      <Routes>
        <Route
          path="/complete-auth"
          element={
            <React.Suspense fallback={<>...</>}>
              <CompleteAuth />
            </React.Suspense>
          }
        />
        <Route
          path="/wallets"
          element={
            <React.Suspense fallback={<>...</>}>
              <Wallets />
            </React.Suspense>
          }
        />
        <Route
          path="/home"
          element={
            <React.Suspense fallback={<>...</>}>
              <HomePage />
            </React.Suspense>
          }
        />
        <Route
          path="/contracts"
          element={
            <React.Suspense fallback={<>...</>}>
              <ContractsPage />
            </React.Suspense>
          }
        />
        <Route
          path="/rankings"
          element={
            <React.Suspense fallback={<>...</>}>
              <CareerPage />
            </React.Suspense>
          }
        />
        <Route
          path="/dashboard"
          element={
            <React.Suspense fallback={<>...</>}>
              <PerformancePage />
            </React.Suspense>
          }
        />
        <Route
          path="/profile"
          element={
            <React.Suspense fallback={<>...</>}>
              <ProfilePage />
            </React.Suspense>
          }
        />
        <Route
          path="/contract/:cid"
          element={
            <React.Suspense fallback={<>...</>}>
              <ContractPage />
            </React.Suspense>
          }
        />
        <Route
          path="/staroftheweek"
          element={
            <React.Suspense fallback={<>...</>}>
              <StarWeekPage />
            </React.Suspense>
          }
        />
        <Route
          path="/view/contracts"
          element={
            <React.Suspense fallback={<>...</>}>
              <PublicContractsPage />
            </React.Suspense>
          }
        />
        <Route
          path="/view/staroftheweek"
          element={
            <React.Suspense fallback={<>...</>}>
              <StarWeekPage />
            </React.Suspense>
          }
        />
        <Route
          path="/view/contract/:cid"
          element={
            <React.Suspense fallback={<>...</>}>
              <PublicContractPage />
            </React.Suspense>
          }
        />
        <Route
          path="/view/rankings"
          element={
            <React.Suspense fallback={<>...</>}>
              <PublicRankingsPage />
            </React.Suspense>
          }
        />
        <Route
          path="/portfolio/:pid/:tid"
          element={
            <React.Suspense fallback={<>...</>}>
              <PortfolioTransactionPage />
            </React.Suspense>
          }
        />
        <Route
          path="/portfolio/create"
          element={
            <React.Suspense fallback={<>...</>}>
              <CreatePortfolioPage />
            </React.Suspense>
          }
        />
        <Route
          path="/portfolio/:pid"
          element={
            <React.Suspense fallback={<>...</>}>
              <PortfolioContainerPage />
            </React.Suspense>
          }
        />
        <Route
          path="/portfolio"
          element={
            <React.Suspense fallback={<>...</>}>
              <PortfolioPage />
            </React.Suspense>
          }
        />
        <Route
          path="/address/:wid"
          element={
            <React.Suspense fallback={<>...</>}>
              <PublicAddressPage />
            </React.Suspense>
          }
        />
        <Route
          path="/"
          element={
            <React.Suspense fallback={<>...</>}>
              <LandPage />
            </React.Suspense>
          }
        />
      </Routes>
      <Footer />
    </>
  );
}
